import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  setDashboards,
  setDefaultDashboard,
  setSelectedDashboard,
  upsertDashboard,
} from "@src/reducers/dashboards-slice";
import {
  getDashboardsV2UseCase,
  GetDashboardsV2Result,
} from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboards-v2-use-case";
import { getDashboardByIdV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboard-by-id-v2-use-case";

// Read-only hook: loads the dashboard list, lazy-loads full
// widgets/positioning for the selected one, exposes loading
// flags. Owner of the canonical state for the editor
// (`/dashboard-builder`); the viewer (`/dashboard`) does not read
// this hook — it loads its own data through `use-viewer-dashboard.ts`.
//
// The list GET is exposed as `listPromise` so that other hooks
// that need the same list response can `await` the same
// promise (the editor-only `useAutoPromoteDefault` hook reads
// it to make its sole-published-default decision without
// issuing a second GET).
//
// The auto-promote-default behaviour (sole published becomes
// default — writes to the BE) lives in `use-auto-promote-default.ts`
// and is invoked only by the editor. Keeping it out of this hook
// keeps this hook read-only by construction; the viewer page
// doesn't need to opt out of anything.
export const useDashboards = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  // Non-null when the most recent failed fetch left it there: either the
  // dashboards-list GET or a GET dashboard-by-id. One field, not two, because
  // two would let the newer error clear back to an older one: the list fails,
  // a detail fails, then a detail retried successfully would resurrect the
  // stale list error into the toast. The `source` tag keeps that single field
  // able to say which request failed, which the canvas needs (a failed list
  // must not hide a dashboard the editor has already loaded).
  //
  // Cleared at the start of every attempt, so it always describes the latest
  // attempt, and set only on failure, so a success leaves the newest failure
  // visible until the next attempt starts.
  const [fetchError, setFetchError] = useState<{
    source: "list" | "detail";
    message: string;
  } | null>(null);

  // Ids whose full widgets/positioning have already been fetched via GET /{id},
  // so selecting them again doesn't re-fetch.
  const loadedDetailIds = useRef<Set<string>>(new Set());

  const authContext = useSelector(
    (s: RootState) => s.yourSlice.authContext,
    shallowEqual,
  );

  const dashboards = useSelector(
    (state: RootState) => state.dashboards.dashboards,
  );
  const selectedDashboardId = useSelector(
    (state: RootState) => state.dashboards.selectedDashboardId,
  );
  const defaultDashboardId = useSelector(
    (state: RootState) => state.dashboards.defaultDashboardId,
  );

  // Fetch the dashboard list (summaries) once on mount. We
  // kick the GET off inside a `useEffect` and store the
  // promise in `useState` so other hooks can `await` the
  // SAME promise (e.g. the editor-only `useAutoPromoteDefault`
  // hook reads `useDashboards().listPromise` instead of
  // issuing a second GET). On logout (`authContext → null`)
  // we clear the promise so consumers see null and
  // early-return.
  //
  // Trade-off: under React 18 StrictMode, the effect runs
  // twice in dev (run → cleanup → re-run by design). The
  // cleanup sets `cancelled` so the first continuation
  // short-circuits its Redox dispatch, but the first HTTP
  // request is NOT aborted (no AbortController on the
  // use case). So in StrictMode dev you'll see two in-flight
  // GETs on mount, with the first one's response discarded.
  // Production runs the effect exactly once. We accept the
  // dev noise rather than introduce a module-level request
  // cache for a StrictMode-only concern (`ponytail: dev-
  // only double-GET noise; introduce request cache if this
  // leaks into prod telemetry`).
  const [listPromise, setListPromise] =
    useState<Promise<GetDashboardsV2Result> | null>(null);

  useEffect(() => {
    // A new list attempt clears the previous LIST error, but must not wipe a
    // DETAIL error for the current selection: on an auth-context swap this
    // effect re-runs while the detail effect does not (its deps are the
    // selection and the dispatcher), so erasing the detail error here would
    // leave nothing loaded, nothing in flight and no error to explain it.
    setFetchError((prev) => (prev?.source === "detail" ? prev : null));
    if (!authContext) {
      setListPromise(null);
      return;
    }
    let cancelled = false;
    const p = getDashboardsV2UseCase();
    setListPromise(p);
    (async () => {
      const result = await p;
      if (cancelled) return;
      if (result.success && result.data) {
        dispatch(setDashboards(result.data));

        const defaultSummary = result.data.find((d) => d.is_default);
        if (defaultSummary) {
          dispatch(setDefaultDashboard(defaultSummary.id));
        }
      } else {
        // The use case never rejects (it catches internally), so a failure only
        // ever surfaces here. Without this the editor silently showed "create
        // or select a dashboard" for a request that never reached the server.
        setFetchError({
          source: "list",
          message: result.error ?? "Failed to fetch dashboards",
        });
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [authContext, dispatch]);

  // Single "select-on-mount" effect. Fires exactly once per
  // mount instance when `defaultDashboardId` becomes available.
  // A ref-guard blocks subsequent re-fires (so a "Set as
  // default" fired from inside the editor never overrides the
  // user's currently-edited dashboard). On remount the ref
  // re-initialises, so navigating away and back always lands
  // the user on the current default — including after visiting
  // `/dashboard`. This collapses the previous auto-select +
  // sync-selection effect pair into one equivalent guard; the
  // difference compared to the dual-effect version is that the
  // editor no longer preserves the user's mid-edit selection
  // across non-viewer round-trips, which the user has pre-
  // approved as the least-complex acceptable behaviour.
  const didInitDefaultSelectionRef = useRef(false);
  useEffect(() => {
    if (didInitDefaultSelectionRef.current) return;
    if (!defaultDashboardId) return;
    didInitDefaultSelectionRef.current = true;
    dispatch(setSelectedDashboard(defaultDashboardId));
  }, [defaultDashboardId, dispatch]);

  // Lazy-load full widgets/positioning for the selected dashboard.
  // The list endpoint only returns summaries, so the full object is fetched
  // the first time a dashboard is opened.
  useEffect(() => {
    // Clear the prior error and the spinner at the top of the effect, not just
    // inside `loadDetails`, because the early-return paths below would
    // otherwise leave the previous selection's error visible on a brand-new
    // selection, or leave the spinner running (a cancelled fetch's own cleanup
    // is skipped on purpose). Selecting an already-cached dashboard, or
    // clearing the selection entirely, both bypass `loadDetails`.
    setFetchError(null);
    setIsDetailsLoading(false);
    if (!selectedDashboardId) return;
    if (loadedDetailIds.current.has(selectedDashboardId)) return;

    let cancelled = false;

    const loadDetails = async () => {
      // Belt-and-braces: also clear at the GET boundary so a future change
      // to the early-return logic can't reintroduce the bug silently.
      setFetchError(null);
      setIsDetailsLoading(true);
      try {
        const result = await getDashboardByIdV2UseCase(selectedDashboardId);
        // A newer selection superseded this fetch. Its own effect run owns the
        // spinner and any error, so this response must not touch either — the
        // old guard only compared ids and could still land a failed fetch's
        // error on the dashboard the user had moved on to.
        if (cancelled) return;
        if (result.success && result.data) {
          dispatch(upsertDashboard(result.data));
          loadedDetailIds.current.add(selectedDashboardId);
        } else {
          setFetchError({
            source: "detail",
            message: result.error ?? "Failed to load dashboard details",
          });
        }
      } finally {
        if (!cancelled) setIsDetailsLoading(false);
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedDashboardId, dispatch]);

  // Synchronously read `loadedDetailIds.current` to expose details-loaded
  // status for the currently-selected dashboard. Reading the ref each
  // render (rather than mirroring it as state) keeps the first render
  // after a selection consistent: the Summary object the List endpoint
  // returned has empty widgets, and `isDetailsLoading` is still its
  // previous value until the effect has had a chance to flip it.
  // Without this gate, the empty Summary briefly flashes the
  // empty-dashboard placeholder before the spinner takes over.
  const isCurrentDetailsLoaded =
    !!selectedDashboardId && loadedDetailIds.current.has(selectedDashboardId);

  return {
    dashboards,
    selectedDashboardId,
    defaultDashboardId,
    isLoading,
    isDetailsLoading,
    fetchError,
    isCurrentDetailsLoaded,
    listPromise,
  };
};
